import React, { useState } from 'react'
import {
  ArrowPathIcon,
  MinusCircleIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline'
import {
  addTicketsToBasket,
  removeTicketsFromBasket
} from '@lib/spektrix/index'
import { showBookingError } from '@lib/booking-error-handler'
import { syncMealDealsWithTickets } from '@lib/meal-deal-sync'
import { useTicketTypes } from '@lib/use-ticket-types'
import useBasketModel from '@lib/basket-model'

const UnreservedAreas = ({
  instance,
  plan,
  unreservedTicketsInBasket,
  mutate,
  hideOnlineAreas = false
}) => {
  const BASKET_DELAY = 1500
  const [updatingBasket, setUpdatingBasket] = useState({})
  const [selectedArea, setSelectedArea] = useState(null)

  // Use global basket model
  const basket = useBasketModel()

  // Filter areas based on hideOnlineAreas setting
  const filteredAreas = plan?.areas?.filter(area => {
    if (hideOnlineAreas && area.name?.toLowerCase() === 'online') {
      return false
    }
    return true
  }) || []

  // Get ticket types from the instance (not the area)
  const ticketTypes = useTicketTypes(
    instance.spektrix_v3_id || instance.id,
    instance.commission_value,
    hideOnlineAreas
  )

  // Group tickets by area
  const ticketsByArea = unreservedTicketsInBasket.reduce((acc, ticket) => {
    // The ticket object structure from basket may vary, so check multiple possible fields
    const areaId = ticket.seatingPlan || ticket.plan || ticket.area
    if (!acc[areaId]) acc[areaId] = []
    acc[areaId].push(ticket)
    return acc
  }, {})

  const AreaSection = ({ area }) => {
    const areaTickets = ticketsByArea[area.id] || []
    const isSelected = selectedArea?.id === area.id || (!selectedArea && plan.areas[0]?.id === area.id)
    
    const handleAreaSelect = () => {
      setSelectedArea(area)
    }

    const TicketOption = ({ option }) => {
      const inBasket = areaTickets.filter(t => t.type === option.value)
      const key = `${area.id}-${option.value}`

      return (
        <div className='flex items-center justify-between py-3'>
          <button
            onClick={() => {
              setUpdatingBasket(prev => ({ ...prev, [key]: true }))
              const toRemove = inBasket[0]
              if (!toRemove) {
                setUpdatingBasket(prev => ({ ...prev, [key]: false }))
                return
              }
              syncMealDealsWithTickets({
                action: 'remove',
                ticketsToProcess: [toRemove.id],
                instanceData: { items: unreservedTicketsInBasket },
                mealDealInfo: instance,
                currentBasket: basket.data
              })
                .then(() => {
                  if (mutate) mutate()
                  setTimeout(() => {
                    setUpdatingBasket(prev => ({ ...prev, [key]: false }))
                  }, BASKET_DELAY)
                })
                .catch(error => {
                  console.error('Remove error:', error)
                  setUpdatingBasket(prev => ({ ...prev, [key]: false }))
                })
            }}
            disabled={updatingBasket[key] || inBasket.length === 0}
            className='disabled:opacity-50 group'
          >
            <MinusCircleIcon className='w-8 h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
          </button>
          <div className='flex items-center space-x-1 text-gray-900 dark:text-white'>
            {updatingBasket[key] ? (
              <ArrowPathIcon className='w-4 h-4 animate-spin mr-2' />
            ) : (
              <span>{inBasket.length > 0 ? `${inBasket.length} x` : ''}</span>
            )}
            <span>{option.label}</span>
          </div>
          <button
            onClick={() => {
              setUpdatingBasket(prev => ({ ...prev, [key]: true }))
              const ticketData = [
                {
                  instance: instance.spektrix_v3_id || instance.id,
                  seatingPlan: area.id,
                  type: option.value
                }
              ]
              syncMealDealsWithTickets({
                action: 'add',
                ticketsToProcess: ticketData,
                instanceData: { items: unreservedTicketsInBasket },
                mealDealInfo: instance,
                currentBasket: basket.data
              })
                .then(() => {
                  if (mutate) mutate()
                  setTimeout(() => {
                    setUpdatingBasket(prev => ({ ...prev, [key]: false }))
                  }, BASKET_DELAY)
                })
                .catch(error => {
                  console.error('Add to basket error:', error)
                  setUpdatingBasket(prev => ({ ...prev, [key]: false }))
                  // Use global showBookingError if available (for priority booking error handling)
                  if (window.showBookingError) {
                    window.showBookingError(error)
                  } else {
                    showBookingError(error)
                  }
                })
            }}
            disabled={updatingBasket[key]}
            className='disabled:opacity-50 group'
          >
            <PlusCircleIcon className='w-8 h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
          </button>
        </div>
      )
    }

    return (
      <div className='mb-6'>
        <button
          onClick={handleAreaSelect}
          className={`w-full text-left p-4 rounded-lg transition-colors ${
            isSelected
              ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
              : 'bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <div className='flex justify-between items-center'>
            <h3 className='font-semibold text-lg text-gray-900 dark:text-white'>
              {area.name}
            </h3>
            {areaTickets.length > 0 && (
              <div className='text-sm font-medium text-blue-600 dark:text-blue-400'>
                {areaTickets.length} ticket{areaTickets.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </button>

        {isSelected && (
          <div className='mt-4 ml-4'>
            {ticketTypes.isLoading ? (
              <div className='text-center py-4'>
                <ArrowPathIcon className='w-6 h-6 animate-spin text-gray-500 mx-auto' />
                <p className='text-sm text-gray-500 mt-2'>Loading ticket types...</p>
              </div>
            ) : ticketTypes.ticketOptions?.length > 0 ? (
              <div className='space-y-2'>
                {ticketTypes.ticketOptions.map(option => (
                  <TicketOption option={option} key={option.value} />
                ))}
              </div>
            ) : (
              <p className='text-sm text-gray-500'>No ticket types available for this area</p>
            )}
          </div>
        )}
      </div>
    )
  }

  // Check if this is a Group plan with unreserved areas
  if (!filteredAreas || filteredAreas.length === 0) {
    return <div>No areas available</div>
  }

  // If only one area remains after filtering, render UnreservedTickets component instead
  if (filteredAreas.length === 1) {
    const UnreservedTickets = require('./unreserved-tickets').default
    // Create a modified plan with the single area's ID as the spektrix_v3_id
    const areaPlan = {
      ...plan,
      spektrix_v3_id: filteredAreas[0].id
    }
    return (
      <UnreservedTickets
        instance={instance}
        plan={areaPlan}
        unreservedTicketsInBasket={unreservedTicketsInBasket}
        ticketOptions={ticketTypes.ticketOptions}
        mutate={mutate}
        instanceData={{
          items: unreservedTicketsInBasket,
          mealDeals: [] // Will be populated from current basket
        }}
      />
    )
  }

  return (
    <div className='space-y-4'>
      <div className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
        Select an area below to view and add tickets:
      </div>
      {filteredAreas.map(area => (
        <AreaSection key={area.id} area={area} />
      ))}
    </div>
  )
}

export default UnreservedAreas